import { useInfiniteQuery } from "@tanstack/react-query"
import { useMemo } from "react"

import useUser from "@hooks/useUser"

import fetchObjectListData from "@utils/fetchObjectListData"

import { ObjectListData, ObjectsData, UseObjectsViewDataProps } from "@organisms/ObjectsView/ObjectsView.types"

export default function useObjectsViewData<T = ObjectsData>(props: UseObjectsViewDataProps) {
    const { tabContentName, activeTab, searchKeywords, filters, sorting } = props

    // The active tab is restored from storage before `useUser` has seeded, so the request waits for the
    // user rather than firing once without everything derived from it.
    const { isLoading: isUserLoading } = useUser()

    const { data, error, isLoading, isError, fetchNextPage, hasNextPage, isFetchingNextPage, refetch } =
        useInfiniteQuery<ObjectListData<T>>({
            queryKey: [
                "tableData",
                tabContentName,
                activeTab?.listEndpoint,
                activeTab?.endpointKwargs,
                searchKeywords,
                // URLSearchParams hashes to an empty object, so the serialized form is what
                // actually keys the query; `filters` itself is here for the exhaustive-deps rule.
                filters,
                filters?.toString(),
                sorting,
            ],
            queryFn: ({ pageParam }) =>
                fetchObjectListData<T>({
                    objectName: tabContentName,
                    endpoint: activeTab?.listEndpoint ?? null,
                    endpointKwargs: activeTab?.endpointKwargs || [],
                    searchKeywords,
                    filters,
                    sorting,
                    nextPage: pageParam as string,
                }),
            initialPageParam: "",
            staleTime: 60000,
            getNextPageParam: (lastPage) => lastPage.next,
            enabled: !!activeTab && !isUserLoading,
        })

    const currentData = useMemo(() => data?.pages.map((page) => page.results).flat(1) || [], [data])

    return {
        currentData,
        error,
        isLoading,
        isError,
        fetchNextPage,
        hasNextPage,
        isFetchingNextPage,
        refetch,
        isEmpty: currentData?.length === 0 && !isLoading,
    }
}
